import {
  ChevronLeftOutlineIcon,
  ChevronRightOutlineIcon,
  FilterOutlineIcon,
} from '@ikyucom/design-system-icons-react'
import { SelectBox } from 'macaron-frontend-ui'
import { useCallback, useEffect } from 'react'
import Skeleton from 'react-loading-skeleton'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import InventoryCalendar from '~/components/calendar/sd/InventoryCalendar'
import InventoryTime from '~/components/calendar/sd/InventoryTime'
import type { ReserveStateDatetime } from '~/features/reserve/state'
import { SystemError } from '~/lib/error/systemError'
import type { DateText, HourMinute } from '~/lib/graphql/scalars'
import { planName } from '~/lib/plan/name'
import { useIsPC } from '~/lib/ua'
import type { CalendarContext } from '~/navigation'
import { css, cx, sva } from '~/styled-system/css'
import {
  scope,
  useCalendar,
  useCalendarQuery,
  useCriteria,
  useFilterLabel,
  useMonthlyCalendar,
  usePlan,
  useReservableVisitorsCounts,
  useReservableVisitorsCountsQuery,
  useSetCriteria,
  useSetState,
  useSetVisitDate,
  useSetVisitorsCount,
  useVisitDate,
  useVisitorsCount,
} from './store'
import type { ReserveCondition } from './type'

type Props = {
  state: ReserveStateDatetime
  context?: CalendarContext
  onClickTime: (condition: ReserveCondition) => void
}

export function VisitCondition({ context, state, onClickTime }: Props) {
  return (
    <scope.Provider>
      <Body context={context} state={state} onClickTime={onClickTime} />
    </scope.Provider>
  )
}

function Body({ context, state, onClickTime }: Props) {
  const { plan, isPending, reservableVisitorsCountsError, calendarError } = useVisitCondition(
    state,
    context
  )

  const isPC = useIsPC()

  if (reservableVisitorsCountsError) {
    throw new SystemError(
      `PlanReservableVisitorsCounts ${reservableVisitorsCountsError.message ?? ''}`
    )
  }
  if (calendarError) {
    throw new SystemError(`PlanInventoryCalendar ${calendarError.message ?? ''}`)
  }
  if (isPending) {
    return <Loading />
  }
  if (plan === undefined) {
    throw new SystemError('予約可能なプランがありません。')
  }

  return (
    <>
      <div className={css({ pb: '[80px]', _pc: { pb: '0' } })}>
        <PlanName plan={plan} />
        <Filter />
        <VisitorsCounts />
        <Calendar />
        {isPC && <PcTimes onClickTime={onClickTime} />}
      </div>
      {!isPC && <SdTimes onClickTime={onClickTime} />}
    </>
  )
}

function useVisitCondition(state: ReserveStateDatetime, context?: CalendarContext) {
  const set = useSetState()
  const setCriteria = useSetCriteria()
  const plan = usePlan()
  const { isPending: reservableVisitorsCountsIsPending, error: reservableVisitorsCountsError } =
    useReservableVisitorsCountsQuery()
  const { isPending: calendarIsPending, error: calendarError } = useCalendarQuery()
  useEffect(() => {
    set(state)
    setCriteria(context)
  }, [context, set, setCriteria, state])

  return {
    plan,
    isPending: reservableVisitorsCountsIsPending || calendarIsPending,
    reservableVisitorsCountsError,
    calendarError,
  }
}

const filterStyles = sva({
  slots: ['container', 'content'],
  base: {
    container: {
      borderBottom: 'medium',
      p: '3',
    },
    content: {
      display: 'flex',
      alignItems: 'center',
      gap: '1',
      borderRadius: 'sm',
      bg: 'background',
      p: '2',
      fontSize: 'sm',
      fontWeight: 'bold',
      color: 'gray.800',
    },
  },
})

function Filter() {
  const label = useFilterLabel()
  if (label === '') {
    return null
  }
  const classes = filterStyles()
  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <FilterOutlineIcon className={css({ color: 'tertiary' })} />
        絞り込み中：{label}
      </div>
    </div>
  )
}

const visitorsCountsStyles = sva({
  slots: ['container'],
  base: {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      px: '3',
      py: '5',
      _pc: {
        px: '4',
        pt: '4',
        pb: '5',
      },
    },
  },
})

function VisitorsCounts() {
  const reservableVisitorsCounts = useReservableVisitorsCounts()
  const visitorsCount = useVisitorsCount()
  const onChange = useSetVisitorsCount()
  const classes = visitorsCountsStyles()
  return (
    <div className={classes.container}>
      <span className={css({ fontWeight: 'bold' })}>人数</span>
      <SelectBox
        width={250}
        values={reservableVisitorsCounts?.map((count) => `${count}名`) ?? []}
        value={`${visitorsCount}名`}
        label={`${visitorsCount}名`}
        onChange={onChange}
        onFocus={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
      />
    </div>
  )
}

const calendarStyles = sva({
  slots: ['container'],
  base: {
    container: {
      px: '3',
      pb: '5',
      _pc: {
        px: '4',
        pb: '4',
      },
    },
  },
})

function Calendar() {
  const monthlyCalendar = useMonthlyCalendar()
  const plan = usePlan()
  const visitDate = useVisitDate()
  const onSelected = useSetVisitDate()
  if (plan === undefined) {
    return null
  }
  const classes = calendarStyles()
  return (
    <div className={classes.container}>
      <Swiper>
        {monthlyCalendar?.map((c, i) => {
          return (
            <SwiperSlide key={i} className="h-full">
              <InventoryCalendar
                inventoryCalendar={c}
                isFirst={i === 0}
                isLast={i === monthlyCalendar.length - 1}
                slideIndex={i}
                onSelected={onSelected}
                selectedDate={visitDate}
                isSeatOnly={plan.isSeatOnly}
              />
            </SwiperSlide>
          )
        })}
      </Swiper>
    </div>
  )
}

const timesStyles = sva({
  slots: ['container', 'swiper', 'button', 'arrow', 'icon'],
  base: {
    container: {
      w: 'full',
      boxShadow: 'outline.lg',
      bg: 'white',
      py: '3',
      position: 'fixed',
      bottom: '0',
      left: '0',
      _pc: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: 'none',
        position: 'sticky',
        borderTop: 'light',
        pt: '3',
        pb: '6',
        px: '4',
      },
    },
    swiper: {
      display: 'flex',
      _sd: {
        // SP版ではSwiperのデフォルトスタイルを上書きして両サイドに余白を持たせる
        paddingX: '3!',
      },
      _pc: {
        w: '[360px]',
      },
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    arrow: {
      display: 'flex',
      w: '[32px]',
      h: '[32px]',
      alignItems: 'center',
      justifyContent: 'center',
      rounded: 'full',
      border: 'medium',
      ':disabled &': {
        border: 'light',
      },
    },
    icon: {
      color: 'tertiary',
      ':disabled * &': {
        color: 'gray.400',
      },
    },
  },
})

function PcTimes({ onClickTime }: { onClickTime: (condition: ReserveCondition) => void }) {
  const calendar = useCalendar()
  const plan = usePlan()
  const visitDate = useVisitDate() as DateText
  const visitorsCount = useVisitorsCount()
  const criteria = useCriteria()
  const onClick = useCallback(
    (visitTime: HourMinute) => {
      onClickTime({
        visitorsCount,
        visitDate,
        visitTime,
        seatTypes: criteria?.seatTypes,
        windowSide: criteria?.windowSide,
      })
    },
    [onClickTime, visitorsCount, visitDate, criteria]
  )

  if (plan === undefined) {
    return null
  }

  const classes = timesStyles()
  return (
    <div className={classes.container}>
      <button
        type="button"
        aria-label="前の時間帯を見る"
        className={cx(classes.button, 'times-prev-slide-button')}
      >
        <span className={classes.arrow}>
          <ChevronLeftOutlineIcon className={classes.icon} width={20} height={20} />
        </span>
      </button>
      <Swiper
        modules={[Navigation]}
        navigation={{
          prevEl: '.times-prev-slide-button',
          nextEl: '.times-next-slide-button',
        }}
        slidesPerGroup={4}
        slidesPerView={4}
        spaceBetween={8}
        className={classes.swiper}
      >
        {calendar
          ?.find((c) => {
            return c.date.date === visitDate
          })
          ?.inventory.map((inventory, index) => {
            return (
              <SwiperSlide key={index}>
                <InventoryTime
                  inventory={inventory}
                  mealPeriod={plan.mealPeriod}
                  onClickTime={onClick}
                  slideIndex={index}
                  isSeatOnly={plan.isSeatOnly}
                />
              </SwiperSlide>
            )
          })}
      </Swiper>
      <button
        type="button"
        aria-label="次の時間帯を見る"
        className={cx(classes.button, 'times-next-slide-button')}
      >
        <span className={classes.arrow}>
          <ChevronRightOutlineIcon className={classes.icon} width={20} height={20} />
        </span>
      </button>
    </div>
  )
}

function SdTimes({ onClickTime }: { onClickTime: (condition: ReserveCondition) => void }) {
  const calendar = useCalendar()
  const plan = usePlan()
  const visitDate = useVisitDate() as DateText
  const visitorsCount = useVisitorsCount()
  const criteria = useCriteria()
  const onClick = useCallback(
    (visitTime: HourMinute) => {
      onClickTime({
        visitorsCount,
        visitDate,
        visitTime,
        seatTypes: criteria?.seatTypes,
        windowSide: criteria?.windowSide,
      })
    },
    [onClickTime, visitorsCount, visitDate, criteria]
  )

  if (plan === undefined) {
    return null
  }

  const classes = timesStyles()
  return (
    <div className={classes.container}>
      <Swiper slidesPerView={4} spaceBetween={8} freeMode={true} className={classes.swiper}>
        {calendar
          ?.find((c) => {
            return c.date.date === visitDate
          })
          ?.inventory.map((inventory, index) => {
            return (
              <SwiperSlide key={index}>
                <InventoryTime
                  inventory={inventory}
                  mealPeriod={plan.mealPeriod}
                  onClickTime={onClick}
                  slideIndex={index}
                  isSeatOnly={plan.isSeatOnly}
                />
              </SwiperSlide>
            )
          })}
      </Swiper>
    </div>
  )
}

const planNameStyles = sva({
  slots: ['container', 'title'],
  base: {
    container: {
      borderBottom: 'medium',
      p: '3',
      _pc: {
        p: '4',
      },
    },
    title: {
      textAlign: 'center',
      fontSize: 'sm',
      _pc: {
        textAlign: 'left',
        fontSize: 'md',
      },
    },
  },
})

function PlanName({ plan }: { plan: undefined | Parameters<typeof planName>[0] }) {
  const classes = planNameStyles()
  return (
    <div className={classes.container}>
      <h2 className={classes.title}>
        {/* 名前がまだない場合でもある程度の高さを確保したいので &nbsp; */}
        {plan ? planName(plan) : <>&nbsp;</>}
      </h2>
    </div>
  )
}

function Loading() {
  const plan = usePlan()
  const visitorsCount = useVisitorsCount()
  return (
    <div className="space-y-5 *:px-3">
      <PlanName plan={plan} />
      <div className="flex items-center justify-between">
        <span className="font-bold">人数</span>
        <SelectBox
          width={250}
          values={[]}
          label={`${visitorsCount}名`}
          onFocus={(e) => {
            e.stopPropagation()
            e.preventDefault()
          }}
        />
      </div>
      <div>
        {/* 100% をきかせるために div で囲む */}
        <Skeleton width="100%" height={400} />
      </div>
    </div>
  )
}
